class CtrPersons extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.mobileBreakpoint = 750;
    this.manualSpeed = 400;
    this.resumeAutoplayTimeout = null;
    this.handleResize = this.handleResize.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
  }

  connectedCallback() {
    if (typeof Swiper === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.unbindControls();
    this.clearResumeAutoplayTimeout();
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  init() {
    this.buildSwiper();
    window.addEventListener('resize', this.handleResize);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  getSpaceBetween() {
    return window.innerWidth < this.mobileBreakpoint ? 16 : 40;
  }

  shouldAutoplay(blockCount) {
    if (blockCount < 2) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return this.dataset.autoplay === 'true';
  }

  clearResumeAutoplayTimeout() {
    if (this.resumeAutoplayTimeout) {
      clearTimeout(this.resumeAutoplayTimeout);
      this.resumeAutoplayTimeout = null;
    }
  }

  resumeAutoplayIfAllowed() {
    const blockCount = parseInt(this.dataset.blockCount, 10) || 0;
    if (!this.shouldAutoplay(blockCount) || !this.swiperInstance?.autoplay) return;
    this.swiperInstance.autoplay.start();
  }

  pauseAutoplayForInteraction() {
    if (!this.swiperInstance?.autoplay) return;

    this.swiperInstance.autoplay.stop();
    this.clearResumeAutoplayTimeout();
    this.resumeAutoplayTimeout = setTimeout(() => {
      this.resumeAutoplayIfAllowed();
    }, 4000);
  }

  unbindControls() {
    const prevButton = this.querySelector('[ref="prev"]');
    const nextButton = this.querySelector('[ref="next"]');

    if (prevButton) {
      prevButton.removeEventListener('click', this.handlePrevClick);
    }

    if (nextButton) {
      nextButton.removeEventListener('click', this.handleNextClick);
    }
  }

  bindControls(prevButton, nextButton) {
    this.unbindControls();

    if (prevButton) {
      prevButton.addEventListener('click', this.handlePrevClick);
    }

    if (nextButton) {
      nextButton.addEventListener('click', this.handleNextClick);
    }
  }

  slideBy(direction) {
    const swiper = this.swiperInstance;
    if (!swiper) return;

    this.pauseAutoplayForInteraction();

    const wasFreeMode = swiper.params.freeMode;
    if (wasFreeMode) {
      swiper.params.freeMode = false;
    }

    if (direction === 'prev') {
      swiper.slidePrev(this.manualSpeed, true);
    } else {
      swiper.slideNext(this.manualSpeed, true);
    }

    if (wasFreeMode) {
      const restoreFreeMode = () => {
        swiper.params.freeMode = true;
        swiper.off('transitionEnd', restoreFreeMode);
      };
      swiper.on('transitionEnd', restoreFreeMode);
    }
  }

  handlePrevClick(event) {
    event.preventDefault();
    this.slideBy('prev');
  }

  handleNextClick(event) {
    event.preventDefault();
    this.slideBy('next');
  }

  buildSwiper() {
    const carousel = this.querySelector('[ref="carousel"]');
    const prevButton = this.querySelector('[ref="prev"]');
    const nextButton = this.querySelector('[ref="next"]');
    const blockCount = parseInt(this.dataset.blockCount, 10) || 0;

    if (!carousel || blockCount < 1 || typeof Swiper === 'undefined') return;

    this.unbindControls();

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const shouldAutoplay = this.shouldAutoplay(blockCount);
    const shouldLoop = blockCount > 1;
    const scrollSpeed = parseInt(this.dataset.speed, 10) || 60000;
    const pauseForInteraction = () => this.pauseAutoplayForInteraction();

    this.swiperInstance = new Swiper(carousel, {
      loop: shouldLoop,
      loopedSlides: shouldLoop ? blockCount : 0,
      loopAdditionalSlides: shouldLoop ? blockCount : 0,
      slidesPerView: 'auto',
      spaceBetween: this.getSpaceBetween(),
      speed: scrollSpeed,
      freeMode: shouldAutoplay,
      freeModeMomentum: true,
      freeModeMomentumRatio: 0.35,
      freeModeMomentumVelocityRatio: 0.35,
      freeModeSticky: false,
      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: true,
      touchRatio: 1,
      touchAngle: 45,
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false,
      touchEventsTarget: 'container',
      threshold: 5,
      longSwipesRatio: 0.25,
      followFinger: true,
      resistance: true,
      resistanceRatio: 0.85,
      preventClicks: true,
      preventClicksPropagation: true,
      autoplay: shouldAutoplay
        ? {
            delay: 1,
            disableOnInteraction: false
          }
        : false,
      breakpoints: {
        750: {
          spaceBetween: 40
        }
      },
      on: {
        touchStart: () => {
          carousel.classList.add('is-touching');
          pauseForInteraction();
        },
        sliderMove: pauseForInteraction,
        touchEnd: () => {
          carousel.classList.remove('is-touching');
          pauseForInteraction();
        }
      }
    });

    this.bindControls(prevButton, nextButton);

    if (shouldAutoplay && this.swiperInstance.autoplay) {
      this.swiperInstance.autoplay.start();
    }
  }

  handleMouseEnter() {
    if (this.swiperInstance?.autoplay) {
      this.swiperInstance.autoplay.stop();
    }
  }

  handleMouseLeave() {
    this.clearResumeAutoplayTimeout();
    this.resumeAutoplayIfAllowed();
  }

  handleResize() {
    if (!this.swiperInstance) return;

    const spaceBetween = this.getSpaceBetween();

    if (this.swiperInstance.params.spaceBetween !== spaceBetween) {
      this.buildSwiper();
      return;
    }

    this.swiperInstance.update();

    if (this.shouldAutoplay(parseInt(this.dataset.blockCount, 10) || 0) && this.swiperInstance.autoplay) {
      this.swiperInstance.autoplay.start();
    }
  }
}

customElements.get('ctr-persons') || customElements.define('ctr-persons', CtrPersons);
